import { APP_INITIALIZER, Inject, InjectionToken, NgModule, isDevMode } from '@angular/core';
import { BrowserModule } from '@angular/platform-browser';
import { AppRoutingModule } from './app-routing.module';
import { AppComponent } from './app.component';
import { ApolloModule, APOLLO_OPTIONS, APOLLO_NAMED_OPTIONS } from 'apollo-angular'
import { HttpLink } from 'apollo-angular/http'
import { InMemoryCache } from '@apollo/client/core'
import { BrowserAnimationsModule } from '@angular/platform-browser/animations';
import { HttpClientModule, HTTP_INTERCEPTORS, HttpClient } from '@angular/common/http';
import { StoreModule } from '@ngrx/store';
import { FormsModule, ReactiveFormsModule } from '@angular/forms';
import { StoreDevtoolsModule } from '@ngrx/store-devtools';
import { environment } from 'src/environments/environment';
import { NZ_I18N } from 'ng-zorro-antd/i18n';
import { en_US } from 'ng-zorro-antd/i18n';
import { AccordionModule } from 'primeng/accordion';
import { DialogModule } from 'primeng/dialog';
import { CascadeSelectModule } from 'primeng/cascadeselect';
import { InputSwitchModule } from 'primeng/inputswitch';
import { AppReducer } from './store/app.reducers';
import { EffectsModule } from '@ngrx/effects';
import { AppEffects } from './store/app.effects';
import { PrimeNGUIModule } from './shared/modules/primeng-ui.module';
import { MessageService } from 'primeng/api';
import { CookieService } from 'ngx-cookie-service';
import { CustomHttpInterceptorInterceptor } from './custom-http-interceptor.interceptor';
import { PriceRoundPipe } from './core/pipes/price-round.pipe';
import { LoadingScreenComponent } from './components/loading-screen/loading-screen.component';
import { HeaderDynamicComponentDirective } from './shared/directives/headerDynamicComponent';
import { FooterDynamicComponentDirective } from './shared/directives/footerDynamicComponent';
import { AppProductNamePipe } from './core/pipes/app-product-name.pipe';
import { NzMessageService } from 'ng-zorro-antd/message';
import { ProductsCellModule } from 'projects/products-cell/src/public-api';
import { HeaderThemesModule } from 'projects/header-themes/src/public-api';
import { FooterThemesModule } from 'projects/footer-themes/src/public-api';
import { NgxGoogleAnalyticsModule, NgxGoogleAnalyticsRouterModule } from 'ngx-google-analytics';
import { SharedModule } from './shared/modules/shared.module';
import { TopLevelComponent } from './components/top-level/top-level.component';
import { SubCategoryModule } from 'projects/subcat-themes/src/lib/categories.module';
import { ErrorInterceptor } from './core/interceptors/error.interceptor';
import { CartModule, CartService } from 'projects/imakeapp-cart/src/public-api';
import { PaymentService } from './Services/PaymentService/payment.service';
import { MyAccountService } from './Services/ProfileService/my-account.service';
import { LanguagePipe, LanguageService, WebsiteAttributesService } from 'imakeapp-shared';
import { MessagesModule } from 'primeng/messages';
import { ToastModule } from 'primeng/toast';
import { CarouselModule } from 'primeng/carousel';
import { DialogService } from 'primeng/dynamicdialog';

import { NgxUiLoaderModule, NgxUiLoaderHttpModule } from "ngx-ui-loader";
import { ServiceWorkerModule } from '@angular/service-worker';

import { LOCALE_ID } from '@angular/core';
import { registerLocaleData } from '@angular/common';
import localeAr from '@angular/common/locales/ar';
import localeEn from '@angular/common/locales/en';

registerLocaleData(localeAr, 'ar');
registerLocaleData(localeEn, 'en');




export function appInitializerFactory(
  languageService: LanguageService,
  attributeThemeService: WebsiteAttributesService
) {
  return () => Promise.all([
    languageService.request_site_languages(),
    attributeThemeService.get_site_theme()
  ]).then(() => {});
}



@NgModule({
  declarations: [
    AppComponent,
    PriceRoundPipe,
    LoadingScreenComponent,
    HeaderDynamicComponentDirective,
    FooterDynamicComponentDirective,
    AppProductNamePipe,
    TopLevelComponent
  ],
  imports: [
    BrowserModule,
    AppRoutingModule,
    BrowserAnimationsModule,
    HttpClientModule,
    FormsModule,
    ReactiveFormsModule,
    AccordionModule,
    DialogModule,
    NgxGoogleAnalyticsModule.forRoot(environment.gaTRACKING),
    NgxGoogleAnalyticsRouterModule,
    ApolloModule,
    CascadeSelectModule,
    InputSwitchModule,
    StoreModule.forRoot({}), // store singleton
    EffectsModule.forRoot([AppEffects]),
    StoreModule.forFeature("General" , AppReducer),
    StoreDevtoolsModule.instrument({
      maxAge: 25,
      logOnly: environment.production,
    }),
    NgxUiLoaderModule,
    NgxUiLoaderHttpModule.forRoot({
      loaderId: "loader",
      showForeground: true,
      // exclude: [
      //   `${environment.endpoint}/RestaurantAttachment/add`,
      //   `${environment.endpoint}/orders/ordersOperation`,
      // ],
    }),

    PrimeNGUIModule,
    CarouselModule,

    FooterThemesModule,
    HeaderThemesModule,
    CartModule,

    ToastModule,
    SharedModule,
    ServiceWorkerModule.register('ngsw-worker.js', {
      enabled: !isDevMode(),
      // Register the ServiceWorker as soon as the application is stable
      // or after 30 seconds (whichever comes first).
      registrationStrategy: 'registerWhenStable:30000'
    }),
  ],
  bootstrap: [AppComponent],
  providers:
  [
    {
      provide: LOCALE_ID,
      useFactory: () => {
        const activeLang = localStorage.getItem('active_lang');
        try {
          const lang = activeLang ? JSON.parse(activeLang).code : null;
          return lang || 'en'; 
        } catch {
          return 'en';
        }
      },
    },
    CookieService,
    PaymentService,
    MyAccountService,
    {
      provide: APP_INITIALIZER,
      useFactory: appInitializerFactory,
      deps: [LanguageService,WebsiteAttributesService],
      multi: true
    },
    { provide: HTTP_INTERCEPTORS, useClass: ErrorInterceptor, multi: true },

    {
      provide: HTTP_INTERCEPTORS,
      useClass: CustomHttpInterceptorInterceptor,
      multi: true,
    },
    {
      provide: NZ_I18N,
      useValue: en_US
    },
    { provide: 'environment', useValue: environment }
,
    {
      provide: APOLLO_NAMED_OPTIONS,
      useFactory(httpLink: HttpLink) {

        return {
          default: /* <-- This settings will be saved as default client */ {
            cache: new InMemoryCache(),
            link: httpLink.create({
              uri:  environment.graphqlendpoint,
            }),
          },
          paymentService: /* <-- These settings will be saved by name: newClientName */ {
            cache: new InMemoryCache(),
            link: httpLink.create({
              uri:  environment.PaymentApiEndPointGraphql,
            }),
          },
          }
      },
      deps: [HttpLink]
    },
    NzMessageService,
DialogService,
    MessageService,LanguagePipe
  ]
})
export class AppModule {}
